import { useEffect, useRef } from 'react';

function isWakeLockSupported(): boolean {
  return 'wakeLock' in navigator;
}

/**
 * Holds a screen wake lock while `active` is true, so the screen/OS does not sleep
 * during a recording or interpreter session.
 */
export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active || !isWakeLockSupported()) {
      return undefined;
    }

    let isEffectActive = true;

    async function requestLock() {
      if (sentinelRef.current || document.visibilityState !== 'visible') {
        return;
      }
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (!isEffectActive) {
          await sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        // The browser auto-releases the lock when the tab is hidden; clear our ref so we re-request on return.
        sentinel.addEventListener('release', () => {
          sentinelRef.current = null;
        });
      } catch {
        // A rejected request (tab hidden, battery saver, unsupported) must not interrupt the session.
      }
    }

    function reacquireWhenVisible() {
      if (document.visibilityState === 'visible') {
        requestLock();
      }
    }

    requestLock();
    document.addEventListener('visibilitychange', reacquireWhenVisible);

    return () => {
      isEffectActive = false;
      document.removeEventListener('visibilitychange', reacquireWhenVisible);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
